import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  IoChatbubbles,
  IoChatbubbleEllipses,
  IoHelpCircle,
  IoClose,
} from "react-icons/io5";
import myAxios from "../../_services/axiosilearnWrapper";
import Config from "../../_config/config";
import UserService from "../../_services/UserService";
import DoubtService from "../../_services/DoubtService";
import { StudentDoubtPanel } from "../../_components/student-doubt";
import CaseDiscussionOverlay from "./CaseDiscussionOverlay";
import "./StudentAssistFab.css";

const ACTIVE_POLL_MS = 8000;
const DOUBT_POLL_MS = 120000;

/**
 * Single "Assist" FAB anchored bottom-right. Owns polling for every kind of
 * help affordance the student has (right now: doubts + all active case
 * discussions across every group they're in). Click the master FAB to reveal
 * a fly-out list, tap an item to open that specific panel.
 *
 * Global: mounted once from main.jsx alongside authenticated shell.
 */
const readSeenTurnIdx = (sessionId) => {
  try {
    const raw = localStorage.getItem(`cdo-last-seen-${sessionId}`);
    if (raw == null) return -1;
    const n = Number(raw);
    return Number.isFinite(n) ? n : -1;
  } catch {
    return -1;
  }
};

const writeSeenTurnIdx = (sessionId, idx) => {
  try {
    localStorage.setItem(`cdo-last-seen-${sessionId}`, String(idx));
  } catch {
    // localStorage may be blocked in private mode — badge just won't persist
  }
};

const StudentAssistFab = () => {
  const enquiryId = UserService.getEnquiryId();
  const studentId = enquiryId || UserService.getUserId();
  const studentName = (() => {
    try {
      const u = JSON.parse(localStorage["user"] || "{}");
      return u.enquiryName || u.name || u.userName || "Student";
    } catch {
      return "Student";
    }
  })();

  const [sessions, setSessions] = useState([]); // active case sessions
  const [doubtUnread, setDoubtUnread] = useState(0);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [openPanel, setOpenPanel] = useState(null); // { type: 'doubt' } | { type: 'case', sessionId }
  // per-session last-seen TurnIdx, hydrated from localStorage
  const [sessionSeen, setSessionSeen] = useState({});

  // Poll active case discussions
  const pollActive = useCallback(async () => {
    if (!enquiryId) return;
    try {
      const res = await myAxios.get(
        `${Config.nodeSiteUrl}v1/flip-class/case-discussion/active`,
        { params: { enquiryId } }
      );
      const data = res.data;
      if (data?.success) {
        const list = data.data?.sessions || [];
        setSessions(list);
        // Hydrate seen-turn map for any new session
        setSessionSeen((prev) => {
          const next = { ...prev };
          for (const s of list) {
            if (next[s.sessionId] == null) {
              next[s.sessionId] = readSeenTurnIdx(s.sessionId);
            }
          }
          return next;
        });
      }
    } catch {
      // Silent — badge is a nice-to-have
    }
  }, [enquiryId]);

  useEffect(() => {
    if (!enquiryId) return undefined;
    pollActive();
    const id = setInterval(pollActive, ACTIVE_POLL_MS);
    return () => clearInterval(id);
  }, [enquiryId, pollActive]);

  // Poll doubt unread count
  const pollDoubtCount = useCallback(async () => {
    if (!studentId) return;
    try {
      const doubts = await DoubtService.getStudentDoubts(studentId, { limit: 20 });
      const seen = JSON.parse(localStorage.getItem("seen-teacher-responses") || "[]");
      const unread = doubts.filter((d) => d.teacherResponse && !seen.includes(d.id));
      setDoubtUnread(unread.length);
    } catch {
      // Silent
    }
  }, [studentId]);

  useEffect(() => {
    if (!studentId) return undefined;
    pollDoubtCount();
    const id = setInterval(pollDoubtCount, DOUBT_POLL_MS);
    return () => clearInterval(id);
  }, [studentId, pollDoubtCount]);

  // Per-session unread = turnCount - lastSeenTurnIdx (turnIdx is 0-based)
  const perSessionUnread = useMemo(() => {
    const map = {};
    for (const s of sessions) {
      const seen = sessionSeen[s.sessionId] ?? -1;
      // turnCount N means turnIdx 0..N-1 exist; unread = N - (seen + 1)
      const unread = Math.max(0, (s.turnCount || 0) - (seen + 1));
      map[s.sessionId] = unread;
    }
    return map;
  }, [sessions, sessionSeen]);

  // Two overlapping signals on the master FAB:
  //   1. `attentionCount` — every active discussion + a bump if any unread
  //      doubt reply exists. Tells the student "you have N things going on"
  //      even if nothing is unread. Shown as a calm badge.
  //   2. `unreadCount` — total unseen turns + unread doubt replies. When > 0
  //      the badge turns red so the student knows there's genuinely new
  //      content, not just old-but-active threads.
  const unreadTurnCount = Object.values(perSessionUnread).reduce(
    (a, b) => a + b,
    0
  );
  const unreadCount = doubtUnread + unreadTurnCount;
  const attentionCount = sessions.length + (doubtUnread > 0 ? 1 : 0);
  const hasUnread = unreadCount > 0;

  const openSession = useMemo(
    () =>
      openPanel?.type === "case"
        ? sessions.find((s) => s.sessionId === openPanel.sessionId) || null
        : null,
    [openPanel, sessions]
  );

  const handleMasterClick = () => {
    if (openPanel) {
      // If a panel is open, tapping the master closes everything
      setOpenPanel(null);
      setFlyoutOpen(false);
      return;
    }
    setFlyoutOpen((v) => !v);
  };

  const handleOpenDoubt = () => {
    setOpenPanel({ type: "doubt" });
    setFlyoutOpen(false);
  };

  const handleOpenCase = (sessionId) => {
    setOpenPanel({ type: "case", sessionId });
    setFlyoutOpen(false);
    // Optimistically mark seen up to current turnCount so the badge clears
    const s = sessions.find((x) => x.sessionId === sessionId);
    if (s) {
      const nextSeen = (s.turnCount || 1) - 1;
      writeSeenTurnIdx(sessionId, nextSeen);
      setSessionSeen((prev) => ({ ...prev, [sessionId]: nextSeen }));
    }
  };

  const handleClosePanel = () => {
    setOpenPanel(null);
    // Doubt panel may have altered seen-teacher-responses; recheck
    pollDoubtCount();
  };

  const handleTurnsSeen = useCallback((sessionId, lastIdx) => {
    writeSeenTurnIdx(sessionId, lastIdx);
    setSessionSeen((prev) => ({ ...prev, [sessionId]: lastIdx }));
  }, []);

  if (!studentId) return null;

  const isPanelOpen = !!openPanel;

  return (
    <>
      {/* Master FAB */}
      <button
        type="button"
        className={`saf-master ${isPanelOpen || flyoutOpen ? "is-open" : ""} ${sessions.length > 0 ? "has-active" : ""}`}
        onClick={handleMasterClick}
        aria-label={
          isPanelOpen
            ? "Close"
            : `Student assistance${
                attentionCount > 0
                  ? ` — ${sessions.length} active discussion${sessions.length !== 1 ? "s" : ""}${
                      hasUnread ? `, ${unreadCount} unread` : ""
                    }`
                  : ""
              }`
        }
        title={
          attentionCount > 0
            ? `${sessions.length} active discussion${sessions.length !== 1 ? "s" : ""}${
                hasUnread ? ` · ${unreadCount} unread` : ""
              }`
            : "Student assistance"
        }
      >
        <span className="saf-master-icon-stack">
          <span className="saf-master-icon saf-master-icon-open">
            <IoChatbubbles size={26} />
          </span>
          <span className="saf-master-icon saf-master-icon-close">
            <IoClose size={28} />
          </span>
        </span>
        {attentionCount > 0 && !isPanelOpen && (
          <span className={`saf-master-badge ${hasUnread ? "hot" : ""}`}>
            {attentionCount > 9 ? "9+" : attentionCount}
          </span>
        )}
      </button>

      {/* Fly-out */}
      <div
        className={`saf-flyout ${flyoutOpen ? "open" : ""}`}
        role="menu"
        aria-hidden={!flyoutOpen}
      >
        {sessions.length === 0 && (
          <div className="saf-flyout-empty">No active discussions</div>
        )}
        {sessions.map((s) => {
          const unread = perSessionUnread[s.sessionId] || 0;
          return (
            <button
              type="button"
              key={s.sessionId}
              className="saf-flyout-item"
              onClick={() => handleOpenCase(s.sessionId)}
              role="menuitem"
            >
              <span className="saf-flyout-icon saf-flyout-icon-case">
                <IoChatbubbleEllipses size={18} />
              </span>
              <span className="saf-flyout-body">
                <span className="saf-flyout-label">
                  {s.groupName || "Case Discussion"}
                </span>
                <span className="saf-flyout-sub">
                  {s.caseTitle || "Case discussion"}
                </span>
              </span>
              {unread > 0 && (
                <span className="saf-flyout-chip hot">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
          );
        })}

        <button
          type="button"
          className="saf-flyout-item"
          onClick={handleOpenDoubt}
          role="menuitem"
        >
          <span className="saf-flyout-icon saf-flyout-icon-doubt">
            <IoHelpCircle size={20} />
          </span>
          <span className="saf-flyout-body">
            <span className="saf-flyout-label">Ask a Doubt</span>
            <span className="saf-flyout-sub">Send a question to your teacher</span>
          </span>
          {doubtUnread > 0 && (
            <span className="saf-flyout-chip">{doubtUnread}</span>
          )}
        </button>
      </div>

      {/* Panels */}
      {openPanel?.type === "case" && openSession && (
        <CaseDiscussionOverlay
          session={openSession}
          enquiryId={enquiryId}
          onClose={handleClosePanel}
          onTurnsSeen={handleTurnsSeen}
        />
      )}
      <StudentDoubtPanel
        isOpen={openPanel?.type === "doubt"}
        onClose={handleClosePanel}
        studentId={studentId}
        studentName={studentName}
      />
    </>
  );
};

export default StudentAssistFab;
